// Paramètres → Modèles : galerie des mises en page PDF, défaut cabinet, éditeur IA avant/après.
import { useState } from 'react';
import { toast } from 'sonner';
import { PDFViewer } from '@react-pdf/renderer';
import { Card } from '../ui/Card';
import { useTenantBranding } from '../../contexts/ThemeContext';
import {
  usePdfLayouts, useSetDefaultPdfLayout, useApplyPdfLayoutTokens, useRevertPdfLayoutTokens,
} from '../../hooks/queries/useDocumentGeneration';
import { proposePdfLayoutModification, resolveLayoutTokens, type PdfLayoutRow } from '../../services/documents/pdfLayoutService';
import type { PdfLayoutTokens } from '../../services/documents/pdfLayoutTokens';
import { markdownToTiptap, tiptapToPdfBlocks } from '../../services/documents/markdownDoc';
import { DocumentPdfDoc, type DocumentPdfProps } from '../documents/DocumentPdf';

const SAMPLE_MD = `**Lettre recommandée avec accusé de réception**

**Objet : Mise en demeure — dossier CIME-2026-0006**

Madame, Monsieur,

Je vous écris en ma qualité de conseil de la société TechSA, dont les factures n°2026-041 et n°2026-052 demeurent impayées malgré une relance du 12 mai 2026.

En conséquence, je vous mets en demeure de payer la somme de 4 500 € dans un délai de quinze jours à compter de la réception de la présente.

À défaut, je me verrai contraint de saisir la juridiction compétente de toute action utile à la préservation des droits de mon client.

Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.`;
const SAMPLE_BLOCKS = tiptapToPdfBlocks(markdownToTiptap(SAMPLE_MD));
const SAMPLE_RECIPIENT = ['SARL Dupont Bâtiment', '12 rue des Artisans', '14000 Caen'];

function buildPdfProps(
  row: PdfLayoutRow,
  layout: PdfLayoutTokens,
  branding: { firm_name: string; logo_url: string | null; color_primary: string; color_gold: string },
): DocumentPdfProps {
  return {
    firmName: branding.firm_name,
    logoUrl: branding.logo_url,
    colorPrimary: branding.color_primary,
    colorGold: branding.color_gold,
    title: row.name,
    recipientLines: SAMPLE_RECIPIENT,
    dateLine: 'Le 21 juillet 2026',
    blocks: SAMPLE_BLOCKS,
    signerName: 'Julien Chatel',
    layout,
  };
}

export function PdfLayoutsSection() {
  const { data: layouts, isLoading } = usePdfLayouts();
  const { branding } = useTenantBranding();
  const setDefault = useSetDefaultPdfLayout();
  const applyTokens = useApplyPdfLayoutTokens();
  const revertTokens = useRevertPdfLayoutTokens();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [proposed, setProposed] = useState<PdfLayoutTokens | null>(null);
  const [proposing, setProposing] = useState(false);

  const selectedRow = layouts?.find((l) => l.id === selectedId);

  const selectRow = (id: string) => {
    setSelectedId(id);
    setInstruction('');
    setProposed(null);
  };

  const requestModification = async () => {
    if (!selectedId || !instruction.trim()) return;
    setProposing(true);
    try {
      const tokens = await proposePdfLayoutModification(selectedId, instruction.trim());
      setProposed(tokens);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'La modification a échoué.');
    } finally {
      setProposing(false);
    }
  };

  const applyProposal = () => {
    if (!selectedRow || !proposed) return;
    applyTokens.mutate(
      { id: selectedRow.id, current: selectedRow.tokens, proposed },
      {
        onSuccess: () => { toast.success('Mise en page mise à jour.'); setProposed(null); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec de l’application.'),
      },
    );
  };

  const revertPrevious = () => {
    if (!selectedRow) return;
    if (!window.confirm('Rétablir la version précédente de cette mise en page ?')) return;
    revertTokens.mutate(selectedRow, {
      onSuccess: () => toast.success('Version précédente rétablie.'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec du rétablissement.'),
    });
  };

  return (
    <div className="max-w-6xl space-y-4">
      <div className="max-w-3xl">
        <h2 className="text-lg font-semibold text-neutral-800">Mises en page</h2>
        <p className="text-sm text-neutral-500">
          Styles de mise en forme des PDF générés. Choisissez le défaut du cabinet, ou demandez à l’IA une modification (aperçu avant/après).
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Chargement…</p>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Colonne gauche : galerie + consigne IA */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {(layouts ?? []).map((l) => (
                <Card
                  key={l.id}
                  className={`cursor-pointer ${selectedId === l.id ? 'ring-2 ring-cime-primary' : ''}`}
                  contentClassName="space-y-2"
                >
                  <div onClick={() => selectRow(l.id)}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-neutral-800">{l.name}</p>
                      {l.is_default && (
                        <span className="shrink-0 rounded bg-cime-gold/20 px-1.5 py-0.5 text-[10px] font-medium text-cime-primary">
                          Défaut cabinet
                        </span>
                      )}
                    </div>
                    {l.description && <p className="text-xs text-neutral-500">{l.description}</p>}
                  </div>
                  {!l.is_default && (
                    <button
                      type="button"
                      className="w-full rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:border-neutral-300 cursor-pointer"
                      disabled={setDefault.isPending}
                      onClick={() =>
                        setDefault.mutate(l.id, {
                          onSuccess: () => toast.success('Mise en page définie par défaut.'),
                          onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec.'),
                        })}
                    >
                      Définir par défaut
                    </button>
                  )}
                </Card>
              ))}
            </div>

            {selectedRow && (
              proposed ? (
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
                    onClick={() => setProposed(null)}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                    disabled={applyTokens.isPending}
                    onClick={applyProposal}
                  >
                    Appliquer
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-sm">
                    <span className="text-neutral-600">Consigne pour l’IA — {selectedRow.name}</span>
                    <textarea
                      rows={2}
                      className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                      value={instruction}
                      onChange={(e) => setInstruction(e.target.value)}
                    />
                  </label>
                  <p className="text-xs text-neutral-500">
                    Décrivez la modification en français — ex. « mets le nom du cabinet plus gros et enlève le filet doré ». La proposition est prévisualisée avant application.
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    {selectedRow.previous_tokens ? (
                      <button
                        type="button"
                        className="rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
                        disabled={revertTokens.isPending}
                        onClick={revertPrevious}
                      >
                        Rétablir la version précédente
                      </button>
                    ) : <span />}
                    <button
                      type="button"
                      className="rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                      disabled={proposing || !instruction.trim()}
                      onClick={requestModification}
                    >
                      {proposing ? 'Génération en cours…' : 'Demander la modification'}
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Colonne droite : aperçu (Actuel / Proposition empilés pendant la comparaison) */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            {selectedRow ? (
              proposed ? (
                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-xs font-medium text-neutral-500">Actuel — {selectedRow.name}</p>
                    <div className="h-[340px] overflow-hidden rounded border border-neutral-200">
                      <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none', minHeight: 340 }}>
                        <DocumentPdfDoc {...buildPdfProps(selectedRow, resolveLayoutTokens(selectedRow), branding)} />
                      </PDFViewer>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-cime-primary">Proposition</p>
                    <div className="h-[340px] overflow-hidden rounded border-2 border-cime-primary/40">
                      <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none', minHeight: 340 }}>
                        <DocumentPdfDoc {...buildPdfProps(selectedRow, proposed, branding)} />
                      </PDFViewer>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mb-1 text-xs font-medium text-neutral-500">Aperçu — {selectedRow.name}</p>
                  <div className="h-[560px] overflow-hidden rounded border border-neutral-200">
                    <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none', minHeight: 560 }}>
                      <DocumentPdfDoc {...buildPdfProps(selectedRow, resolveLayoutTokens(selectedRow), branding)} />
                    </PDFViewer>
                  </div>
                </div>
              )
            ) : (
              <div className="flex h-[560px] items-center justify-center rounded border border-dashed border-neutral-300 text-sm text-neutral-400">
                Sélectionnez un style pour voir l’aperçu.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
